import React, { useRef, useEffect } from 'react';
import RoundsView from './Rounds.view';
import NewGameState from '../../_types/NewGameState';
import RoundState from '../../_types/RoundState';

type Props = {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  setShouldDisableNextOrFinishButton: (should: boolean) => void;
  scrollToTop?: () => void;
};

const RoundsContainer: React.FC<Props> = ({
  newGameState,
  onChangeNewGameState,
  setShouldDisableNextOrFinishButton,
  scrollToTop,
}) => {
  const tabsRef = useRef<HTMLDivElement>(null);

  // Calculate the number of rounds needed based on the number of competitors
  const calculateTotalRounds = (totalCompetitors: number): number => {
    if (totalCompetitors <= 0) return 0;
    return Math.ceil(Math.log2(totalCompetitors));
  };

  // Function to check if a round has meaningful reward configuration (vs just defaults)
  const hasConfiguredRewards = (round: RoundState | undefined): boolean => {
    if (!round) return false;

    // Check if reward settings have been meaningfully configured by the user
    // Don't count basic currency/type changes as "configured" since those can be defaults
    const hasRewardCounts = !!(
      round.rewardCount1 ||
      round.rewardCount2 ||
      round.rewardCount3 ||
      round.rewardCountP
    );
    const hasCustomRewards = !!(
      round.showCustomReward1 ||
      round.showCustomReward2 ||
      round.showCustomReward3
    );
    const hasRewardLimit = !!(round.rewardLimit && round.rewardLimit !== '');
    const hasSpecificRewards = !!(
      round.rewardID1 ||
      round.rewardID2 ||
      round.rewardID3 ||
      round.rewardIDp
    );

    // Only consider it "configured" if user has actually set specific rewards, counts, or limits
    return (
      hasRewardCounts ||
      hasCustomRewards ||
      hasRewardLimit ||
      hasSpecificRewards
    );
  };

  // Create default round state with auto-populated name and copy settings from previous round
  const createDefaultRoundState = (
    roundIdx: number,
    previousRound?: RoundState
  ): Partial<RoundState> => {
    const baseState = {
      bracketName: `Round ${roundIdx + 1}`,
      startDate: '',
      endDate: '',
      // Copy from previous round or use defaults
      useMultipleKPIs: previousRound?.useMultipleKPIs ?? false,
      KPIRuleID: previousRound?.KPIRuleID ?? '',
      KPIRuleName: previousRound?.KPIRuleName ?? '',
      tieKPIRuleID: previousRound?.tieKPIRuleID ?? '',
      tieKPIRuleName: previousRound?.tieKPIRuleName ?? '',
      isActionCountCustom: previousRound?.isActionCountCustom ?? false,
      actionCount: previousRound?.actionCount ?? '0',
      actionCountType: previousRound?.actionCountType ?? 'count',
      actionCountTypeNum: previousRound?.actionCountTypeNum ?? 0,
      // Copy mission rules from previous round
      missionRules: previousRound?.missionRules
        ? [...previousRound.missionRules]
        : [],
      rules: previousRound?.rules ? [...previousRound.rules] : [],
      // Reward settings (copy all reward preferences from previous round)
      rewardDisposition: previousRound?.rewardDisposition ?? 'winner',
      rewardWhen: previousRound?.rewardWhen ?? 'mission',
      rewardType: previousRound?.rewardType ?? 'mission_reward',
      rewardCurrency: previousRound?.rewardCurrency ?? 'reward',
      // Copy reward counts and custom settings
      rewardCount1: previousRound?.rewardCount1 ?? '',
      rewardCount2: previousRound?.rewardCount2 ?? '',
      rewardCount3: previousRound?.rewardCount3 ?? '',
      rewardCountP: previousRound?.rewardCountP ?? '',
      showCustomReward1: previousRound?.showCustomReward1 ?? false,
      showCustomReward2: previousRound?.showCustomReward2 ?? false,
      showCustomReward3: previousRound?.showCustomReward3 ?? false,
      // Keep reward IDs and names empty for new rounds
      rewardID1: '',
      rewardName1: '',
      rewardID2: '',
      rewardName2: '',
      rewardID3: '',
      rewardName3: '',
      rewardIDp: '',
      rewardNameP: '',
    };

    return baseState;
  };

  useEffect(() => {
    console.log('[REWARD-DEBUG] Rounds initialization triggered:', {
      totalCompetitors: newGameState.bracket.totalCompetitors,
      currentTotalRounds: newGameState.totalRounds,
      currentRoundValues:
        newGameState.bracket.roundValues?.map((round, idx) => ({
          roundIdx: idx + 1,
          hasKPI: !!round.KPIRuleID,
          hasConfiguredRewards: hasConfiguredRewards(round),
          rewardType: round.rewardType,
          rewardCurrency: round.rewardCurrency,
          rewardCount1: round.rewardCount1,
        })) || [],
    });

    if (newGameState.bracket.totalCompetitors <= 0) return;

    const totalRounds = calculateTotalRounds(
      newGameState.bracket.totalCompetitors
    );

    if (totalRounds !== newGameState.totalRounds || !newGameState.totalRounds) {
      console.log('[REWARD-DEBUG] Creating/updating rounds:', {
        totalRounds,
        currentTotalRounds: newGameState.totalRounds,
        needsRoundCreation: true,
      });

      const newGameStateCopy = { ...newGameState };

      // Initialize or extend roundValues array with auto-populated names
      if (!newGameStateCopy.bracket.roundValues) {
        newGameStateCopy.bracket.roundValues = [];
      }

      // Ensure we have roundValues for all rounds with auto-populated names
      while (newGameStateCopy.bracket.roundValues.length < totalRounds) {
        const roundIdx = newGameStateCopy.bracket.roundValues.length;
        const previousRound =
          roundIdx > 0
            ? newGameStateCopy.bracket.roundValues[roundIdx - 1]
            : undefined;

        newGameStateCopy.bracket.roundValues.push(
          createDefaultRoundState(roundIdx, previousRound) as RoundState
        );
      }

      // If we have more roundValues than needed, trim the array
      if (newGameStateCopy.bracket.roundValues.length > totalRounds) {
        newGameStateCopy.bracket.roundValues =
          newGameStateCopy.bracket.roundValues.slice(0, totalRounds);
      }

      // Update existing rounds that don't have names with auto-populated names
      // and copy settings from previous rounds if they don't have them
      newGameStateCopy.bracket.roundValues =
        newGameStateCopy.bracket.roundValues.map((round, index) => {
          const previousRound =
            index > 0
              ? newGameStateCopy.bracket.roundValues[index - 1]
              : undefined;

          // Check if copying is needed based on KPIs or rewards
          const needsKPICopy =
            (!round.KPIRuleID || round.KPIRuleID === '') &&
            previousRound?.KPIRuleID &&
            previousRound.KPIRuleID !== '';

          const needsRewardCopy =
            previousRound &&
            hasConfiguredRewards(previousRound) &&
            !hasConfiguredRewards(round);

          const needsUpdate =
            !round.bracketName ||
            round.bracketName === '' ||
            needsKPICopy ||
            needsRewardCopy;

          if (needsUpdate) {
            const willCopySettings =
              (needsKPICopy || needsRewardCopy) && previousRound;

            if (willCopySettings) {
              console.log('[REWARD-COPY-DEBUG] Round initialization copying:', {
                roundIndex: index + 1,
                needsKPICopy,
                needsRewardCopy,
                previousRoundRewards: {
                  rewardType: previousRound.rewardType,
                  rewardCurrency: previousRound.rewardCurrency,
                  rewardWhen: previousRound.rewardWhen,
                  rewardCount1: previousRound.rewardCount1,
                },
              });
            }

            return {
              ...round,
              bracketName: round.bracketName || `Round ${index + 1}`,
              // Copy settings from previous round if needed
              ...(willCopySettings
                ? {
                    useMultipleKPIs: previousRound.useMultipleKPIs,
                    KPIRuleID: previousRound.KPIRuleID,
                    KPIRuleName: previousRound.KPIRuleName,
                    tieKPIRuleID: previousRound.tieKPIRuleID,
                    tieKPIRuleName: previousRound.tieKPIRuleName,
                    isActionCountCustom: previousRound.isActionCountCustom,
                    actionCount: previousRound.actionCount,
                    actionCountType: previousRound.actionCountType,
                    actionCountTypeNum: previousRound.actionCountTypeNum,
                    missionRules: previousRound.missionRules
                      ? [...previousRound.missionRules]
                      : [],
                    rules: previousRound.rules ? [...previousRound.rules] : [],
                    rewardDisposition: previousRound.rewardDisposition,
                    rewardWhen: previousRound.rewardWhen,
                    rewardType: previousRound.rewardType,
                    rewardCurrency: previousRound.rewardCurrency,
                    rewardCount1: previousRound.rewardCount1,
                    rewardCount2: previousRound.rewardCount2,
                    rewardCount3: previousRound.rewardCount3,
                    rewardCountP: previousRound.rewardCountP,
                    showCustomReward1: previousRound.showCustomReward1,
                    showCustomReward2: previousRound.showCustomReward2,
                    showCustomReward3: previousRound.showCustomReward3,
                  }
                : {}),
            };
          }
          return round;
        });

      onChangeNewGameState({
        ...newGameStateCopy,
        totalRounds,
        currentRound: newGameState.currentRound || 1,
      });
    }
  }, [newGameState.bracket.totalCompetitors]);

  // Ensure the main game KPIRuleID is set from the last round's KPI
  useEffect(() => {
    // Don't proceed if we don't have round values or totalRounds
    if (!newGameState.bracket.roundValues || !newGameState.totalRounds) return;

    const lastRoundIndex = newGameState.totalRounds - 1;
    const lastRound = newGameState.bracket.roundValues[lastRoundIndex];
    const isLastRound = newGameState.currentRound === newGameState.totalRounds;

    // When we're on the last round, make sure we sync values to parent
    if (isLastRound) {
      if (lastRound) {
        // Sync data from rounds to main state
        onChangeNewGameState({
          ...newGameState,
          KPIRuleID: lastRound.KPIRuleID || newGameState.KPIRuleID,
          bracketName:
            newGameState.bracket.roundValues[0]?.bracketName ||
            newGameState.bracketName,
          // Set top-level endDate to last round's endDate for validation
          endDate: lastRound.endDate || newGameState.endDate,
        });
      }
    } else if (lastRound && lastRound.KPIRuleID && !newGameState.KPIRuleID) {
      // Update the main game KPI to match the last round's KPI
      onChangeNewGameState({
        ...newGameState,
        KPIRuleID: lastRound.KPIRuleID,
      });
    }

    // Always sync dates from rounds to prevent validation issues
    const firstRound = newGameState.bracket.roundValues[0];
    const needsDateSync =
      (lastRound &&
        lastRound.endDate &&
        lastRound.endDate !== newGameState.endDate) ||
      (firstRound &&
        firstRound.startDate &&
        firstRound.startDate !== newGameState.startDate);

    if (needsDateSync) {
      const syncData: any = { ...newGameState };

      // Only sync startDate if first round has one
      if (firstRound?.startDate) {
        syncData.startDate = firstRound.startDate;
      }

      // Only sync endDate if last round has a valid one (not empty)
      if (lastRound?.endDate && lastRound.endDate !== '') {
        syncData.endDate = lastRound.endDate;
      }

      onChangeNewGameState(syncData);
    }
  }, [
    newGameState.bracket.roundValues,
    newGameState.totalRounds,
    newGameState.currentRound,
  ]);

  return (
    <RoundsView
      newGameState={newGameState}
      onChangeNewGameState={onChangeNewGameState}
      tabsRef={tabsRef}
      setShouldDisableNextOrFinishButton={setShouldDisableNextOrFinishButton}
      scrollToTop={scrollToTop}
    />
  );
};

export default RoundsContainer;
